import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { apiFetch } from "../../utils/api";
import { WalletTx } from "./types";
import { API, errMsg, fmtAmt, fmtDate, Badge, ErrorBox, KIND_COLORS, STATUS_COLORS } from "./shared";

// Every wallet row of one round, oldest first, with the raw callback payload.
export default function RoundModal({ roundId, onClose }: { roundId: string; onClose: () => void }) {
  const [rows, setRows] = useState<WalletTx[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ transactions: WalletTx[] }>(`${API}/rounds/${encodeURIComponent(roundId)}`)
      .then((d) => setRows(d.transactions || []))
      .catch((e) => setError(errMsg(e)));
  }, [roundId]);

  return (
    <div className="fixed inset-0 z-[1300] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0E1831] border border-[#1E2D55] rounded-xl w-full max-w-4xl max-h-[85vh] overflow-auto text-[#F9F9F9]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#1E2D55]">
          <h3 className="font-semibold">Round <span className="font-mono text-[#4A85F7]">{roundId}</span></h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#162140]" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <ErrorBox error={error} />
          {!rows && !error && <p className="text-[#878AA2] text-sm">Loading…</p>}
          {rows && rows.map((r) => (
            <div key={r.id} className="rounded-lg border border-[#1E2D55] bg-[#0C0D1D] p-3 text-sm">
              <div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
                <Badge text={r.kind} map={KIND_COLORS} />
                <span className={STATUS_COLORS[r.status] || ""}>{r.status}</span>
                <span className="font-mono">{fmtAmt(r.amount_major)} {r.currency}</span>
                <span className="text-[#878AA2]">balance after {fmtAmt(r.balance_after)}</span>
                <span className="text-[#878AA2]">{fmtDate(r.created_at)}</span>
                {r.error_code && <span className="text-[#E01B4F]">{r.error_code}</span>}
              </div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-[#878AA2] font-mono break-all">
                <div>provider tx: {r.provider_tx_id}</div>
                <div>our tx: {r.operator_tx_id}</div>
                {r.reference_tx_id && <div>reference: {r.reference_tx_id}</div>}
                <div>session: {r.session_id || "—"}</div>
              </div>
              {r.payload !== undefined && r.payload !== null && (
                <details className="mt-2">
                  <summary className="text-xs text-[#4A85F7] cursor-pointer">Callback payload</summary>
                  <pre className="mt-2 text-xs bg-[#070815] p-2 rounded overflow-auto">{JSON.stringify(r.payload, null, 2)}</pre>
                </details>
              )}
            </div>
          ))}
          {rows && rows.length === 0 && <p className="text-[#878AA2] text-sm">No wallet rows for this round.</p>}
        </div>
      </div>
    </div>
  );
}
